import {z} from 'zod'

export const customerContactRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    created_at: z.string(),
    is_assigned: z.boolean().optional().default(false),
})

/**
 * Minimal customer contact reference returned by project/job endpoints.
 * Excludes `is_assigned` and `created_at` since those are customer-detail only.
 */
export const customerContactRefRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
})

export const customerContactRefSchema = customerContactRefRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    title: raw.title,
    description: raw.description,
    email: raw.email,
    phone: raw.phone,
}))

export const customerContactSourceSchema = z.enum(['job', 'project'])

export type CustomerContactRefRaw = z.infer<typeof customerContactRefRawSchema>
export type CustomerContactRef = z.infer<typeof customerContactRefSchema>
export type CustomerContactSource = z.infer<typeof customerContactSourceSchema>

export const customerListItemRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    contact_count: z.number(),
})

export const customerDetailRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    payment_terms: z.string().nullable(),
    contacts: z.array(customerContactRawSchema),
})

export const customerContactSchema = customerContactRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    title: raw.title,
    description: raw.description,
    email: raw.email,
    phone: raw.phone,
    createdAt: raw.created_at,
    isAssigned: raw.is_assigned,
}))

export const customerListItemSchema = customerListItemRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    contactCount: raw.contact_count,
}))

export const customerDetailSchema = customerDetailRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    paymentTerms: raw.payment_terms,
    contacts: raw.contacts.map((contact) => customerContactSchema.parse(contact)),
}))

export const createCustomerRequestSchema = z.object({
    name: z.string().trim().min(1),
    slug: z.string().trim().toUpperCase().optional(),
    contact_name: z.string().trim().min(1),
    phone: z.string().trim().nullable().optional(),
    email: z.string().trim().nullable().optional(),
    title: z.string().trim().nullable().optional(),
    description: z.string().trim().nullable().optional(),
})

export const updateCustomerRequestSchema = z.object({
    name: z.string().trim().min(1),
    slug: z.string().trim().toUpperCase().optional(),
})

export const contactWriteRequestSchema = z.object({
    name: z.string().trim().min(1),
    phone: z.string().trim().nullable().optional(),
    email: z.string().trim().nullable().optional(),
    title: z.string().trim().nullable().optional(),
    description: z.string().trim().nullable().optional(),
})

export type CustomerContactRaw = z.infer<typeof customerContactRawSchema>
export type CustomerListItemRaw = z.infer<typeof customerListItemRawSchema>
export type CustomerDetailRaw = z.infer<typeof customerDetailRawSchema>
export type CustomerContact = z.infer<typeof customerContactSchema>
export type CustomerListItem = z.infer<typeof customerListItemSchema>
export type CustomerDetail = z.infer<typeof customerDetailSchema>

export type CustomerListFilters = {
    name?: string
}

export type CreateCustomerInput = {
    name: string
    slug?: string
    contactName: string
    phone?: string
    email?: string
    title?: string
    description?: string
}

export type UpdateCustomerInput = {
    name: string
    slug?: string
}

export type ContactWriteInput = {
    name: string
    phone?: string
    email?: string
    title?: string
    description?: string
}

export function toCreateCustomerRequest(input: CreateCustomerInput) {
    return createCustomerRequestSchema.parse({
        name: input.name,
        slug: input.slug,
        contact_name: input.contactName,
        phone: input.phone?.trim() || null,
        email: input.email?.trim() || null,
        title: input.title?.trim() || null,
        description: input.description?.trim() || null,
    })
}

export function toUpdateCustomerRequest(input: UpdateCustomerInput) {
    return updateCustomerRequestSchema.parse({
        name: input.name,
        slug: input.slug,
    })
}

export function toContactWriteRequest(input: ContactWriteInput) {
    return contactWriteRequestSchema.parse({
        name: input.name,
        phone: input.phone?.trim() || null,
        email: input.email?.trim() || null,
        title: input.title?.trim() || null,
        description: input.description?.trim() || null,
    })
}

export function createEmptyContact(): ContactWriteInput {
    return {
        name: '',
        phone: '',
        email: '',
        title: '',
        description: '',
    }
}

const customerImportPreviewRowRawSchema = z.object({
    row_index: z.number(),
    customer_name: z.string(),
    contact_name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable().default(null),
    title: z.string().nullable().default(null),
    description: z.string().nullable().default(null),
    action: z.enum(['create_customer_and_contact', 'add_contact']),
    status: z.enum(['valid', 'invalid']),
    error: z.string().nullable().default(null),
    warning: z.string().nullable().default(null),
})

export const customerImportPreviewRowSchema = customerImportPreviewRowRawSchema.transform(
    (raw) => ({
        rowIndex: raw.row_index,
        customerName: raw.customer_name,
        contactName: raw.contact_name,
        phone: raw.phone,
        email: raw.email,
        title: raw.title,
        description: raw.description,
        action: raw.action,
        status: raw.status,
        error: raw.error,
        warning: raw.warning,
    }),
)

export type CustomerImportPreviewRowRaw = z.infer<typeof customerImportPreviewRowRawSchema>
export type CustomerImportPreviewRow = z.infer<typeof customerImportPreviewRowSchema>

const customerImportPreviewRawSchema = z.object({
    current_template_version: z.string(),
    template_outdated: z.boolean(),
    warning: z.string().nullable().default(null),
    rows: z.array(customerImportPreviewRowRawSchema),
})

export const customerImportPreviewSchema = customerImportPreviewRawSchema.transform((raw) => ({
    currentTemplateVersion: raw.current_template_version,
    templateOutdated: raw.template_outdated,
    warning: raw.warning,
    rows: raw.rows.map((row) => customerImportPreviewRowSchema.parse(row)),
}))

export type CustomerImportPreviewRaw = z.infer<typeof customerImportPreviewRawSchema>
export type CustomerImportPreview = z.infer<typeof customerImportPreviewSchema>

const customerImportErrorRawSchema = z.object({
    row_index: z.number(),
    message: z.string(),
})

export const customerImportErrorSchema = customerImportErrorRawSchema.transform((raw) => ({
    rowIndex: raw.row_index,
    message: raw.message,
}))

export type CustomerImportErrorRaw = z.infer<typeof customerImportErrorRawSchema>
export type CustomerImportError = z.infer<typeof customerImportErrorSchema>

const customerImportResultRawSchema = z.object({
    imported_count: z.number(),
    skipped_count: z.number(),
    customers_created_count: z.number(),
    current_template_version: z.string(),
    template_outdated: z.boolean(),
    warning: z.string().nullable().default(null),
    errors: z.array(customerImportErrorRawSchema),
})

export const customerImportResultSchema = customerImportResultRawSchema.transform((raw) => ({
    importedCount: raw.imported_count,
    skippedCount: raw.skipped_count,
    customersCreatedCount: raw.customers_created_count,
    currentTemplateVersion: raw.current_template_version,
    templateOutdated: raw.template_outdated,
    warning: raw.warning,
    errors: raw.errors.map((error) => customerImportErrorSchema.parse(error)),
}))

export type CustomerImportResultRaw = z.infer<typeof customerImportResultRawSchema>
export type CustomerImportResult = z.infer<typeof customerImportResultSchema>
